import React, { useEffect, useState } from "react";
import {
    LottieLoadingIndicator,
    PortalFormBody,
    PortalFormSection,
    PortalFormSectionDescription,
    PortalFormSectionTitle,
    PortalHighlightMetricCard,
    PortalIconActionButton,
    PortalMetricCard,
    PortalMetricGrid,
    PortalMetricLabel,
    PortalMetricValue,
    PortalSearchInput,
    PortalTableActions,
    PortalTableControls,
    PortalTableToolbar,
    StandardModal,
    StandardModalActionButton,
    StandardModalFixedLayout,
    StandardModalScrollArea,
    StandardModalText,
    Toast
} from "src/components";
import MenuIcon from "src/img/menu.svg";
import ListIcon from "src/img/list.svg";
import FileIcon from "src/img/notice/document.svg";
import {
    STORAGE_FILE_CATEGORY,
    deleteStorageFile,
    formatStorageGb,
    formatStorageFileSize,
    formatStoragePercentage,
    formatStorageSize,
    getStorageFileDownload,
    getStorageFilePreview,
    getStorageFiles
} from "../api/storageApi";
import {
    AudioIcon,
    DocumentIcon,
    EmptyState,
    FileGrid,
    FileMenu,
    FileMenuButton,
    FileMenuItem,
    FileName,
    FileSize,
    FileThumb,
    FileTile,
    PlayBadge,
    PreviewBody,
    SectionTitle,
} from "../forms/StorageManagement.style";

function CategorySummary({ categories }) {
    return (
        <PortalMetricGrid columns="2" gap="0.75rem">
            {categories.map(category => (
                <PortalMetricCard key={category.key || category.label} minHeight="6.5rem" padding="0.75rem">
                    <PortalMetricLabel>{category.label}</PortalMetricLabel>
                    <PortalMetricValue size="1.25rem">{category.count.toLocaleString()}</PortalMetricValue>
                    <StandardModalText size="0.75rem">
                        {formatStoragePercentage(category.percentage)}% used
                    </StandardModalText>
                </PortalMetricCard>
            ))}
        </PortalMetricGrid>
    );
}

function UsageSummary({ summary }) {
    const usedLabel = formatStorageSize(summary.usedBytes);
    const freeLabel = formatStorageSize(summary.freeBytes);
    const percentageLabel = formatStoragePercentage(summary.usagePercentage);

    return (
        <PortalHighlightMetricCard minHeight="10rem" padding="1rem">
            <PortalMetricLabel>Storage allowance</PortalMetricLabel>
            <PortalMetricValue size="1.5rem">{formatStorageGb(summary.limitGb)} GB</PortalMetricValue>
            <StandardModalText size="0.8125rem">
                {usedLabel} used ({percentageLabel}%). {freeLabel} remaining.
            </StandardModalText>
        </PortalHighlightMetricCard>
    );
}

function FilePreviewContent({ file }) {
    if (file.type === "image" && file.previewUrl) {
        return <img src={file.previewUrl} alt={file.name} />;
    }

    if (file.type === "video") {
        return (
            <>
                {file.previewUrl && <video src={file.previewUrl} />}
                <PlayBadge />
            </>
        );
    }

    if (file.type === "audio") {
        return <AudioIcon />;
    }

    return <div style={{ width: "30px", height: "auto" }}> <img src={FileIcon}/> </div>;
}

function FilePreviewModal({ file, onClose }) {
    if (!file) return null;

    return (
        <StandardModal
            open={Boolean(file)}
            onClose={onClose}
            widthMode="full"
            title={file.name}
            description="Preview stored media before returning to storage management."
            bodyPadding="0"
        >
            <PreviewBody>
                {file.type === "image" && file.previewUrl && <img src={file.previewUrl} alt={file.name} />}
                {file.type === "video" && file.previewUrl && <video src={file.previewUrl} controls />}
                {file.type === "audio" && file.previewUrl && <audio src={file.previewUrl} controls />}
                {(!file.previewUrl || file.type === STORAGE_FILE_CATEGORY.DOCUMENT) && <DocumentIcon />}
            </PreviewBody>
        </StandardModal>
    );
}

export default function ManageStorageModal({ show, onClose, onPurchase, summary, onChanged }) {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [viewMode, setViewMode] = useState("grid");
    const [openMenuId, setOpenMenuId] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);

    const loadFiles = async (searchValue = search) => {
        setLoading(true);
        try {
            const response = await getStorageFiles({ search: searchValue });
            setFiles(response);
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!show) return undefined;

        const timeout = setTimeout(() => {
            loadFiles(search);
        }, 300);

        return () => clearTimeout(timeout);
    }, [show, search]);

    const previewManagedFile = async file => {
        // Images, videos and audio use the preview modal
        if (["image", "video", "audio"].includes(file.type)) {
            if (file.previewUrl) {
                setPreviewFile(file);
                return;
            }

            try {
                const response = await getStorageFilePreview(file.id);
                setPreviewFile({
                    ...file,
                    previewUrl: response.previewUrl || response.url || ""
                });
            } catch (error) {
                Toast.error(error.getApiErrorDetails().message);
            }

            return;
        }

        // Documents -> open in new tab
        try {
            const response = await getStorageFilePreview(file.id);
            const url = response.previewUrl || response.url;

            if (!url) {
                Toast.error("Preview unavailable.");
                return;
            }

            window.open(url, "_blank", "noopener,noreferrer");
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
        }
    };

    const downloadFile = async file => {
        let downloadUrl = file.downloadUrl;

        if (!downloadUrl) {
            try {
                const response = await getStorageFileDownload(file.id);
                downloadUrl = response.downloadUrl || response.url;
            } catch (error) {
                Toast.error(error.getApiErrorDetails().message);
                return;
            }
        }

        if (!downloadUrl) {
            Toast.error("This file is not available for download.");
            return;
        }

        try {
            const response = await fetch(downloadUrl);
            const blob = await response.blob();

            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();

            a.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.log(error)
            Toast.error("Unable to download file.");
        }
    };

    const deleteFile = async file => {
        if (!file.canDelete) {
            Toast.error("This file cannot be deleted from storage management.");
            return;
        }

        const confirmed = window.confirm(`Delete ${file.name}? This can affect the content it is attached to.`);
        if (!confirmed) return;

        try {
            await deleteStorageFile(file.id);
            setFiles(current => current.filter(item => item.id !== file.id));
            if (onChanged) onChanged();
            Toast.info("File deleted.");
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
        }
    };

    const handlePurchase = () => {
        onClose();
        if (onPurchase) onPurchase();
    };

    return (
        <>
            <StandardModal
                open={show}
                onClose={onClose}
                widthMode="full"
                title="Manage Storage"
                description="Review storage usage, browse your business files, and free up space when needed."
                bodyPadding="0"
                paperStyle={{ height: "min(52rem, calc(100vh - 2rem))" }}
            >
                <StandardModalFixedLayout>
                    <PortalFormBody
                        columns="18rem minmax(0, 1fr)"
                        gap="0"
                        padding="0"
                        height="100%"
                        overflow="hidden"
                    >
                        <PortalFormSection
                            background="var(--calendar-surface-secondary, #F3F5FB)"
                            border="0"
                            radius="0"
                            padding="1.2rem"
                            overflow="auto"
                        >
                            <PortalFormSectionTitle>Storage summary</PortalFormSectionTitle>
                            <PortalFormSectionDescription>
                                Your storage use across all business files.
                            </PortalFormSectionDescription>
                            <UsageSummary summary={summary} />
                            <CategorySummary categories={summary.categories} />
                            <StandardModalActionButton type="button" width="100%" minWidth="0" $variant="primary" onClick={handlePurchase}>
                                Purchase More Storage
                            </StandardModalActionButton>
                        </PortalFormSection>

                        <PortalFormSection border="0" radius="0" padding="0" overflow="hidden">
                            <PortalTableToolbar radius="0" shadow="none" minHeight="4.25rem">
                                <PortalTableControls>
                                    <PortalSearchInput
                                        value={search}
                                        onChange={event => setSearch(event.target.value)}
                                        onKeyDown={event => {
                                            if (event.key === "Enter") {
                                                loadFiles(search);
                                            }
                                        }}
                                        placeholder="Search site files..."
                                        width="100%"
                                        minWidth="0"
                                    />
                                </PortalTableControls>
                                <PortalTableActions>
                                    <PortalIconActionButton
                                        type="button"
                                        width="2.625rem"
                                        minWidth="2.625rem"
                                        padding="0"
                                        backgroundColor={viewMode === "grid" ? undefined : "#FFFFFF"}
                                        color={viewMode === "grid" ? undefined : "#171A22"}
                                        border={viewMode === "grid" ? undefined : "1px solid #D8DEEC"}
                                        aria-label="Grid view"
                                        onClick={() => setViewMode("grid")}
                                    >
                                        <img src={MenuIcon} width="20" height="20" alt="" />
                                    </PortalIconActionButton>
                                    <PortalIconActionButton
                                        type="button"
                                        width="2.625rem"
                                        minWidth="2.625rem"
                                        padding="0"
                                        backgroundColor={viewMode === "list" ? undefined : "#FFFFFF"}
                                        color={viewMode === "list" ? undefined : "#171A22"}
                                        border={viewMode === "list" ? undefined : "1px solid #D8DEEC"}
                                        aria-label="List view"
                                        onClick={() => setViewMode("list")}
                                    >
                                        <img src={ListIcon} width="20" height="20" alt="" />
                                    </PortalIconActionButton>
                                </PortalTableActions>
                            </PortalTableToolbar>

                            <StandardModalScrollArea padding="1.25rem">
                                <SectionTitle>Site Files</SectionTitle>
                                {loading && <LottieLoadingIndicator />}
                                {!loading && files.length === 0 && (
                                    <EmptyState>No files found.</EmptyState>
                                )}
                                {!loading && files.length > 0 && (
                                    <FileGrid viewMode={viewMode}>
                                        {files.map(file => (
                                            <FileTile
                                                key={file.id}
                                                viewMode={viewMode}
                                                onClick={() => previewManagedFile(file)}
                                            >
                                                <FileThumb viewMode={viewMode}>
                                                    <FilePreviewContent file={file} />
                                                </FileThumb>
                                                <div>
                                                    <FileName viewMode={viewMode}>{file.name}</FileName>
                                                    <FileSize viewMode={viewMode}>{formatStorageFileSize(file.sizeBytes)}</FileSize>
                                                </div>
                                                <FileMenuButton
                                                    type="button"
                                                    viewMode={viewMode}
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        setOpenMenuId(openMenuId === file.id ? null : file.id);
                                                    }}
                                                >
                                                  &#8943;
                                                </FileMenuButton>
                                                {openMenuId === file.id && (
                                                    <FileMenu viewMode={viewMode} onClick={event => event.stopPropagation()}>
                                                        <FileMenuItem type="button" onClick={() => previewManagedFile(file)}>
                                                            Preview
                                                        </FileMenuItem>
                                                        <FileMenuItem type="button" onClick={() => downloadFile(file)}>
                                                            Download
                                                        </FileMenuItem>
                                                        {file.canDelete && (
                                                            <FileMenuItem type="button" danger onClick={() => deleteFile(file)}>
                                                                Delete
                                                            </FileMenuItem>
                                                        )}
                                                    </FileMenu>
                                                )}
                                            </FileTile>
                                        ))}
                                    </FileGrid>
                                )}
                            </StandardModalScrollArea>
                        </PortalFormSection>
                    </PortalFormBody>
                </StandardModalFixedLayout>
            </StandardModal>

            <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
        </>
    );
}
